/**
 * Écran d’accueil `/` — reflet de la progression **serveur** (DECISIONS §4.2, §9.3, §1.3 A-14).
 *
 * Ce que l’accueil affiche :
 * - le **solde d’éclats** et le **niveau maximum atteint**, lus dans `GET /me` : le navigateur ne
 *   calcule ni ne corrige jamais un montant (DECISIONS §8, `docs/api-v1.md` §11.1) ;
 * - les **points de collection** (`pointsCollection`), également fournis par `/me`. Le nombre de
 *   cartes possédées viendra de `GET /me/collection`, qui appartient au jalon M3 : tant qu’il
 *   n’existe pas, l’accueil n’invente aucun compteur de cartes ;
 * - les accès aux **Améliorations**, à l’**Historique** et au jeu.
 *
 * **Aucune partie n’est reprise en cours** (CDC §7, DECISIONS §7.4) : l’accueil n’annonce donc rien
 * sur une run interrompue. L’encart « Une partie est en cours… » a été retiré (carte U-02) : il
 * décrivait une reprise possible qui n’existe pas et a induit le porteur en erreur. L’écran se
 * contente de **purger l’état de run résiduel** de l’onglet (marqueur de session et store en
 * mémoire) pour qu’un lancement suivant ne se croie pas en reprise.
 *
 * Le manifeste est lu pour trouver le niveau 1 par son numéro, jamais par un identifiant codé en
 * dur : un contenu republié reste jouable.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  chargerManifeste,
  recupererProfil,
  useSessionStore,
  versErreurApi,
  type ErreurApi,
} from '../api';
import { formaterNombre } from '../i18n';
import { purgerMarqueurRun } from '../jeu/marqueur-run';
import { reinitialiserRun } from '../jeu/store';
import { BandeauErreur } from '../ui/BandeauErreur';
import { Bouton } from '../ui/Bouton';
import { Chargement } from '../ui/Chargement';
import { Entete } from '../ui/Entete';
import { FriseCampagne } from '../ui/FriseCampagne';
import { PiedDePage } from '../ui/PiedDePage';

import type { Manifest, MeResponse } from '@elemora/shared/api';

/** Données nécessaires à l’accueil : profil d’autorité et manifeste. */
interface DonneesAccueil {
  readonly profil: MeResponse;
  readonly manifeste: Manifest;
}

export function EcranAccueil() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useSessionStore((etat) => etat.user);
  const progression = useSessionStore((etat) => etat.progression);
  const definirUtilisateur = useSessionStore((etat) => etat.definirUtilisateur);
  const definirProgression = useSessionStore((etat) => etat.definirProgression);

  const [manifeste, setManifeste] = useState<Manifest | null>(null);
  const [erreurApi, setErreurApi] = useState<ErreurApi | null>(null);
  const [tentative, setTentative] = useState(0);
  const chargementRef = useRef<{ cle: string; promesse: Promise<DonneesAccueil> } | null>(null);

  /**
   * Charge le profil et le manifeste, en réutilisant la promesse déjà en vol.
   *
   * Même raison que dans l’écran de jeu : StrictMode exécute l’effet deux fois en développement,
   * et deux exécutions ne doivent produire qu’un seul aller-retour réseau.
   *
   * L’historique des runs n’est **plus** lu ici (carte U-02) : aucune partie n’est reprise
   * (CDC §7), l’accueil n’a donc rien à dire d’une run interrompue, et une lecture de moins est
   * une occasion de moins d’afficher un état qui n’existe pas.
   */
  function chargerDonnees(cle: string): Promise<DonneesAccueil> {
    const existante = chargementRef.current;
    if (existante !== null && existante.cle === cle) return existante.promesse;
    const promesse = (async (): Promise<DonneesAccueil> => {
      const [profil, contenu] = await Promise.all([recupererProfil(), chargerManifeste()]);
      return { profil, manifeste: contenu };
    })();
    chargementRef.current = { cle, promesse };
    return promesse;
  }

  useEffect(() => {
    let actif = true;
    chargerDonnees(`${tentative}`)
      .then((donnees) => {
        if (!actif) return;
        definirUtilisateur(donnees.profil.user);
        definirProgression(donnees.profil.progression);
        setManifeste(donnees.manifeste);
        setErreurApi(null);
      })
      .catch((erreur: unknown) => {
        if (!actif) return;
        setErreurApi(versErreurApi(erreur));
      });
    return () => {
      actif = false;
    };
  }, [tentative, definirUtilisateur, definirProgression]);

  /**
   * Purge de l’**état de run résiduel** (carte U-02).
   *
   * L’accueil est l’endroit où l’on constate qu’aucune partie n’est en cours : ni le marqueur de
   * session posé par l’écran de jeu, ni le store de run en mémoire ne doivent y survivre. Sans
   * cela, un marqueur oublié (onglet restauré, fermeture brutale, rechargement) ferait afficher
   * « Run perdue » au lancement suivant, alors que le joueur n’a interrompu aucune partie.
   *
   * Aucune valeur de progression n’est touchée : le marqueur ne contient qu’un identifiant de run
   * et le solde affiché reste celui de `/me`. Purger ici ne crée, ne reprend et ne clôt aucune run
   * côté serveur — l’accueil ne fait que cesser de s’appuyer sur un état local périmé.
   */
  useEffect(() => {
    purgerMarqueurRun();
    reinitialiserRun();
  }, []);

  if (user === null || progression === null) {
    return (
      <div className="ecran ecran--centree">
        <Entete avecDeconnexion />
        <Chargement />
      </div>
    );
  }

  const niveauUn = manifeste?.levels.find((niveau) => niveau.numero === 1) ?? null;
  /**
   * Niveau du **mode illimité**, trouvé par son mode dans le manifeste — jamais par un identifiant
   * codé en dur : un contenu republié reste jouable (§7.6).
   */
  const niveauIllimite = manifeste?.levels.find((niveau) => niveau.mode === 'illimite') ?? null;
  /** Niveaux de la campagne (mode limité), pour la frise de progression. */
  const niveauxCampagne = manifeste?.levels.filter((niveau) => niveau.mode === 'limite') ?? [];
  const chargement = manifeste === null && erreurApi === null;

  return (
    <div className="ecran ecran--accueil">
      <Entete avecDeconnexion />
      <main className="accueil">
        <header className="accueil__entete">
          <div className="accueil__salut">
            <h1 className="accueil__titre">{t('accueil.titre', { pseudo: user.pseudo })}</h1>
            <p className="accueil__sousTitre">{t('accueil.sousTitre')}</p>
          </div>
          <dl className="reserve">
            <div className="reserve__ligne">
              <dt className="reserve__etiquette">{t('accueil.eclats')}</dt>
              <dd className="reserve__valeur" data-eclats={progression.eclats}>
                {formaterNombre(progression.eclats, i18n.language)}
              </dd>
              <dd className="reserve__aide">{t('accueil.eclatsAide')}</dd>
            </div>
            <div className="reserve__ligne">
              <dt className="reserve__etiquette">{t('accueil.pointsCollection')}</dt>
              <dd className="reserve__valeur" data-points-collection={progression.pointsCollection}>
                {formaterNombre(progression.pointsCollection, i18n.language)}
              </dd>
              <dd className="reserve__aide">{t('accueil.pointsCollectionAide')}</dd>
            </div>
          </dl>
        </header>

        {erreurApi === null ? null : (
          <BandeauErreur
            erreur={erreurApi}
            surReessai={() => {
              setTentative((valeur) => valeur + 1);
            }}
          />
        )}

        <section className="panneau campagne" aria-labelledby="titre-campagne">
          <div className="campagne__tete">
            <div className="campagne__titres">
              <h2 className="campagne__titre" id="titre-campagne">
                {t('accueil.campagne.titre')}
              </h2>
              <p className="campagne__texte">{t('accueil.campagne.texte')}</p>
            </div>
            <p className="campagne__record">
              <span className="campagne__record-valeur" data-niveau-max={progression.niveauMax}>
                {formaterNombre(progression.niveauMax, i18n.language)}
              </span>
              <span className="campagne__record-etiquette">
                <span className="campagne__record-titre">{t('accueil.niveauMaximum')}</span>
                <span className="campagne__record-aide">{t('accueil.niveauMaximumAide')}</span>
              </span>
            </p>
          </div>

          {niveauxCampagne.length === 0 ? null : (
            <FriseCampagne niveaux={niveauxCampagne} niveauMax={progression.niveauMax} />
          )}

          <div className="campagne__actions">
            {chargement ? <Chargement /> : null}
            <Bouton
              variante="principal"
              disabled={niveauUn === null}
              onClick={() => {
                if (niveauUn === null) return;
                navigate(`/jeu/${niveauUn.id}`);
              }}
            >
              {t('accueil.jouerNiveau1')}
            </Bouton>
            {manifeste !== null && niveauUn === null ? (
              <p className="note">{t('accueil.niveauIndisponible')}</p>
            ) : null}
          </div>
        </section>

        <div className="accueil__suite">
          <section className="panneau illimite" aria-labelledby="titre-illimite">
            <h2 className="illimite__titre" id="titre-illimite">
              {t('accueil.illimite.titre')}
            </h2>
            {niveauIllimite === null ? null : (
              <p className="illimite__texte" data-mode-illimite-aide="oui">
                {t('accueil.modeIllimiteAide')}
              </p>
            )}
            {manifeste !== null && niveauIllimite === null ? (
              <p className="note">{t('accueil.illimiteIndisponible')}</p>
            ) : null}
            <Bouton
              variante="secondaire"
              disabled={niveauIllimite === null}
              onClick={() => {
                if (niveauIllimite === null) return;
                navigate(`/jeu/${niveauIllimite.id}`);
              }}
            >
              {t('accueil.jouerIllimite')}
            </Bouton>
          </section>

          <ul className="raccourcis" aria-label={t('commun.navigation')}>
            <li className="raccourci">
              <div className="raccourci__texte">
                <h2 className="raccourci__titre">{t('accueil.grimoire')}</h2>
                <p className="raccourci__aide">{t('accueil.grimoireAide')}</p>
              </div>
              <Bouton variante="discret" onClick={() => navigate('/grimoire')}>
                {t('accueil.versGrimoire')}
              </Bouton>
            </li>
            <li className="raccourci">
              <div className="raccourci__texte">
                <h2 className="raccourci__titre">{t('accueil.ameliorations')}</h2>
                <p className="raccourci__aide">{t('accueil.ameliorationsAide')}</p>
              </div>
              <Bouton variante="discret" onClick={() => navigate('/ameliorations')}>
                {t('accueil.versAmeliorations')}
              </Bouton>
            </li>
            <li className="raccourci">
              <div className="raccourci__texte">
                <h2 className="raccourci__titre">{t('accueil.historique')}</h2>
                <p className="raccourci__aide">{t('accueil.historiqueAide')}</p>
              </div>
              <Bouton variante="discret" onClick={() => navigate('/historique')}>
                {t('accueil.versHistorique')}
              </Bouton>
            </li>
          </ul>
        </div>
      </main>
      <PiedDePage />
    </div>
  );
}
